import { QuickView } from './quick-view'
import { BaseComponent } from '../../components/base-component'
import { CartAPI } from '../../api/cart-api'
import { fetchConfig } from '../../utils/fetch-config'
import { CartDrawer } from '../cart-drawer/cart-drawer'
import { CartPage } from '../cart/cart'
import { BodyElement } from 'src/scripts/components/body-element/body-element'
import { CartNotificationPopup } from 'src/scripts/components/cart-notification/cart-notification-popup'
import { $el, $list } from 'src/scripts/utils/utils'
import { parseHTML, replaceNodeChildren } from 'src/scripts/utils/dom'

const CART_DRAWER_SELECTOR = `#CartDrawer`
const CART_PAGE_SELECTOR = 'cart-page'
const CART_NOTIFICATION_POPUP_SELECTOR = '#CartNotificationPopup'
const FORM_SELECTOR = 'form'
const VARIANT_INPUT_SELECTOR = `[data-product-form-variant]`
const QUICK_VIEW = '#Quick-view'
const BODY_ELEMENT_SELECTOR = 'body-element'

export class ProductForm extends BaseComponent {
  cartApi: CartAPI = new CartAPI()
  lastParsedState: string

  mountComponent(): void {
    const form = $el(FORM_SELECTOR, this)
    this.addListener(form, 'submit', this.handleFormSubmit)
  }

  handleFormSubmit = async (event: SubmitEvent): Promise<void> => {
    event.preventDefault()

    const config = this.getQueryConfig()

    this.setDisable(true)
    this.setLoading(true)

    await this.cartApi
      .add(config)
      .then(state => {
        this.setDisable(false)
        this.setLoading(false)
        const parsedState = JSON.parse(state)

        if (parsedState.description) {
          this.showError(parsedState)
          return
        }

        this.lastParsedState = parsedState
        this.updateCartItems(parseHTML(JSON.parse(state).sections[this.cartId]))
      })
      .catch(() => console.log('Error in product form component'))
  }

  updateCartItems(html) {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)
    const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)

    if (cart) {
      this.updateCart(html)
    } else if (cartDrawer) {
      this.updateCartDrawer(html)
    } else if (popup) {
      this.updatePopup(html)
    } else {
      this.updateCartCount(html)
    }
  }

  updateCartCount(html): void {
    const newCount = $el('#NewCartCount', html)
    const currentCounts = $list('#CartCount')

    this.onUpdateCartItems(() => {
      currentCounts.forEach(currentCount => {
        replaceNodeChildren(currentCount, newCount)
      })
    })
  }

  updateCart(html): void {
    const cart: CartPage = $el(CART_PAGE_SELECTOR)

    cart?.updateEmptyStatus(false)
    this.onUpdateCartItems(() => {
      cart?.purchaseHandler(html, this.lastParsedState)
    })
  }

  updateCartDrawer(html): void {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)

    cartDrawer?.updateAsideProductsByParsedState(this.lastParsedState)
    cartDrawer?.updateEmptyStatus()
    this.onUpdateCartItems(() => {
      cartDrawer?.purchaseHandler(html, this.lastParsedState)
    })
  }

  updatePopup(html): void {
    this.onUpdateCartItems(() => {
      const popup: CartNotificationPopup = $el(CART_NOTIFICATION_POPUP_SELECTOR)
      popup?.purchaseHandler(html, this.lastParsedState)
    })
  }

  async onUpdateCartItems(callback): Promise<void> {
    const quickView: QuickView = $el(QUICK_VIEW)

    if (quickView.isOpen) {
      quickView.hide().then(callback)
    } else {
      callback()
    }
  }

  showError(parsedState) {
    const bodyElement: BodyElement = $el(BODY_ELEMENT_SELECTOR)

    const error =
      typeof parsedState.description === 'object'
        ? this.getErrorFromObject(parsedState.description)
        : parsedState.description

    bodyElement.showNotification(error, 'warning')
  }

  getErrorFromObject(errorObject: object) {
    return Object.keys(errorObject)
      .map(key => {
        return `${key}: ${errorObject[key]}`
      })
      .join(' , ')
  }

  getQueryConfig() {
    const form: HTMLFormElement = $el(FORM_SELECTOR, this)
    const config: any = fetchConfig('javascript')
    const formData: any = new FormData(form)

    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    formData.append('sections', [this.cartId])
    formData.append('sections_url', window.location.pathname)
    config.body = formData

    return config
  }

  setLoading(isLoading: boolean): void {
    $list('button[type="submit"]', this).forEach(button =>
      button.classList.toggle('loading', isLoading),
    )
  }

  setDisable(isDisable: boolean): void {
    $list('button[type="submit"]', this).forEach(button =>
      button.toggleAttribute('disabled', isDisable),
    )
  }

  setButtonText(text: string): void {
    $list('button[type="submit"]', this).forEach(
      button => (button.textContent = text),
    )
  }

  getVeriantInput() {
    const form = $el(FORM_SELECTOR, this)
    const variantInput = $el(VARIANT_INPUT_SELECTOR, form)

    return variantInput
  }

  get cartId(): string {
    const cartDrawer: CartDrawer = $el(CART_DRAWER_SELECTOR)
    const cart: CartPage = $el(CART_PAGE_SELECTOR)

    if (cartDrawer) {
      return cartDrawer.dataset.sectionId
    } else if (cart) {
      return cart.dataset.sectionId
    } else {
      return 'cart-notification-popup-content'
    }
  }
}
